import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym, fmtDate } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { DownloadSimple, Warning, ArrowRight } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

export default function ProcurementEfd() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/reports/efd-pending").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const cols = [
    { key: "ref", label: "Invoice / PO" }, { key: "supplier", label: "Supplier" },
    { key: "lx_po_no", label: "PO" }, { key: "date", label: "Date" },
    { key: "amount", label: "Amount", format: (v) => fmtNum(v) },
  ];
  return (
    <Layout>
      <PageHeader title="Pending Procurement EFD"
        subtitle="Local supplier invoices posted without a valid EFD receipt — the input-VAT control on the procurement side. Imports are excluded (no domestic EFD)."
        actions={<Button variant="outline" data-testid="export-procurement-efd" onClick={() => exportToCsv("pending-procurement-efd", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>}
      />
      <div className="space-y-6 p-8">
        <KpiStrip cols={2}>
          <KpiCard icon={Warning} label="Invoices missing EFD" value={rows.length} tone={rows.length ? "warning" : "success"} testid="kpi-procurement-efd-count" />
          <KpiCard icon={Warning} label="Value at risk (input-VAT exposure)" value={`TSh ${fmtNum(total)}`} testid="kpi-procurement-efd-value" />
        </KpiStrip>
        {rows.length === 0 ? (
          <EmptyState icon={Warning} testid="procurement-efd-empty" title="All local procurement EFDs on file" message="Every non-import supplier invoice has a valid EFD receipt recorded." />
        ) : (
          <div className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="procurement-efd-table">
            <table className="w-full text-sm">
              <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                <th className="px-5 py-2 text-left">Invoice / PO</th>
                <th className="px-5 py-2 text-left">Supplier</th>
                <th className="px-5 py-2 text-left">PO</th>
                <th className="px-5 py-2 text-left">Date</th>
                <th className="px-5 py-2 text-right">Amount</th>
                <th className="px-5 py-2 text-right">Action</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`procurement-efd-row-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-2 font-medium">{r.ref || "—"}</td>
                    <td className="px-5 py-2">{r.supplier || "—"}</td>
                    <td className="px-5 py-2">{r.lx_po_no || "—"}</td>
                    <td className="px-5 py-2 lx-num">{fmtDate(r.date)}</td>
                    <td className="px-5 py-2 text-right lx-num">{curSym(r.currency)} {fmtNum(r.amount)}</td>
                    <td className="px-5 py-2 text-right">{r.transaction_id
                      ? <button className="inline-flex items-center gap-1 rounded-sm bg-[#1B4383] px-2 py-1 text-xs font-medium text-white hover:bg-[#122E5C]" data-testid={`procurement-efd-fix-${r.id}`} onClick={() => navigate(`/transactions/${r.transaction_id}?tab=fulfillment`)}>Add EFD<ArrowRight size={12} /></button>
                      : <span className="text-xs text-gray-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
